import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxDataGridModule, DxTextBoxModule } from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { previewLinkLabel } from 'src/app/components/contracts-pill/contracts-pill.component';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { ViewOption, ViewToggleComponent } from 'src/app/components/view-toggle/view-toggle.component';
import { ProjectsCellComponent } from '../../contracts/contract-overview/projects-cell/projects-cell.component';
import {
  SocietyListItem,
  SocietyOrgChartEdge,
  SocietyOrgChartNode,
  SocietySalesModel,
  SocietyService,
} from 'src/app/services/society.service';
import { DEMO_ORG_CHART_EDGES, DEMO_ORG_CHART_NODES, DEMO_SALES_MODELS } from './society-org-chart/org-chart-demo-data';
import { SocietyOrgChartComponent } from './society-org-chart/society-org-chart.component';
import { ContactsAddSocietyPageComponent } from '../contacts-add-society.page/contacts-add-society.page';

const projectLabels = (row: SocietyListItem): string[] => (row?.projects ?? []).map(previewLinkLabel);

/** "Gesellschaften" tab under Kontakte. */
@Component({
    selector: 'app-contacts-societies-page',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, DxTextBoxModule, GpdButtonComponent, ProjectsCellComponent, ViewToggleComponent, SocietyOrgChartComponent],
    templateUrl: './contacts-societies.page.html',
    styleUrls: ['./contacts-societies.page.css']
})
export class ContactsSocietiesPageComponent implements OnInit {

  searchTerm: string = '';
  societies: SocietyListItem[] = [];

  // "Liste | Organigramm", kept as ?view=orgchart so the Kontakte tabs stay and a reload keeps it.
  view: 'list' | 'orgchart' = 'list';

  readonly views: ViewOption[] = [
    { icon: 'list-view', key: 'list', tooltip: $localize`:@@showAsList:Show as list` },
    { icon: 'hierarchy', key: 'orgchart', tooltip: $localize`:@@showAsOrgChart:Show as org chart` },
  ];
  orgChartNodes: SocietyOrgChartNode[] = [];
  orgChartEdges: SocietyOrgChartEdge[] = [];
  orgChartSalesModels: SocietySalesModel[] = [];
  private orgChartLoaded = false;
  /**
   * ?demo=1: example data instead of the API, to look at the org chart without real Gesellschaften.
   * Deliberately no button: users could take the invented structure for real data.
   */
  demo = false;

  // The list stores keys; show the same texts as the form's dropdowns.
  readonly legalFormText = (row: SocietyListItem) =>
    ContactsAddSocietyPageComponent.legalForms.find(o => o.key === row.legalForm)?.text ?? row.legalForm;
  readonly statusText = (row: SocietyListItem) =>
    ContactsAddSocietyPageComponent.societyStates.find(o => o.key === row.status)?.text ?? row.status;

  // Projects column, as on the contract table: the search matches the joined labels, the
  // header filter lists each project once and matches it exactly.
  projectHeaderFilterOptions: { text: string; value: string }[] = [];

  readonly projectsCellValue = (row: SocietyListItem): string => projectLabels(row).join(', ');

  // `function`, not an arrow: DevExtreme calls it with the column as `this`.
  readonly projectsFilterExpression = function (this: any, value: any, operation: string, target: string) {
    if (target === 'headerFilter') {
      return [(row: SocietyListItem) => projectLabels(row).includes(value), '=', true];
    }
    return this.defaultCalculateFilterExpression(value, operation, target);
  };

  constructor(
    private appGlobals: AppGlobals,
    private router: Router,
    private route: ActivatedRoute,
    private societyService: SocietyService
  ) { }

  ngOnInit(): void {
    this.appGlobals.SetGlobalMenuIndex(6);
    this.route.queryParamMap.subscribe(params => {
      this.view = params.get('view') === 'orgchart' ? 'orgchart' : 'list';
      const demo = params.get('demo') === '1';
      // Switching between example and real data loads the chart again.
      if (demo !== this.demo) this.orgChartLoaded = false;
      this.demo = demo;
      if (this.view === 'orgchart') this.loadOrgChart();
    });
    this.loadSocieties();
  }

  changeView(view: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: view === 'orgchart' ? 'orgchart' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async loadOrgChart(): Promise<void> {
    if (this.orgChartLoaded) return;
    this.orgChartLoaded = true;
    if (this.demo) {
      this.orgChartNodes = DEMO_ORG_CHART_NODES;
      this.orgChartEdges = DEMO_ORG_CHART_EDGES;
      this.orgChartSalesModels = DEMO_SALES_MODELS;
      return;
    }
    const result = await this.societyService.getOrgChart().catch(error => ({ hasError: true, feedback: error, nodes: [], edges: [], salesModels: [] }));
    if (result?.hasError) {
      this.orgChartLoaded = false;
      AppGlobals.ShowErrorMessage(result.feedback);
      return;
    }
    this.orgChartNodes = result.nodes ?? [];
    this.orgChartEdges = result.edges ?? [];
    this.orgChartSalesModels = result.salesModels ?? [];
  }

  private async loadSocieties(): Promise<void> {
    const result = await this.societyService.getSocieties().catch(error => ({ hasError: true, feedback: error, societies: [] }));
    if (result?.hasError) {
      AppGlobals.ShowErrorMessage(result.feedback);
      return;
    }
    this.societies = result.societies ?? [];
    this.projectHeaderFilterOptions = [...new Set(this.societies.flatMap(projectLabels))]
      .sort((a, b) => a.localeCompare(b))
      .map(label => ({ text: label, value: label }));
  }

  showDetails(e: any) {
    if (e.rowType !== 'data') return;
    // A project pill (or its "+N") opens the project, not the Gesellschaft.
    if ((e.event?.target as HTMLElement | undefined)?.closest?.('app-projects-cell')) return;
    this.router.navigate(['./contacts/society/' + e.data.id]);
  }

  goToAddSociety() {
    this.router.navigate(['./contacts/addSociety']);
  }
}
